/**
 * circle-gen — 康軒數學三下「第 6 單元 圓」考卷產生器。
 * 對照 `mul-div-gen.ts` / `stats-table-gen.ts` / `time-exam-gen.ts` 同一套 lockdown 架構
 *(**seeded RNG + 策展閉集 / 受控變數 + 程式可驗證答案 + validator + 200-seed regression**)。
 *
 * 「圓」是**幾何 / 概念單元**(不是純計算),grounded 在康軒三下第 6 章課本真實內容
 *(boss 拍攝 p69–83 + 練習百分百六),五個題型區塊:
 *   ① convert 半徑↔直徑換算(✅ 程式可驗證,唯一真相)—— 直徑 = 半徑×2、半徑 = 直徑÷2。
 *      含整數與 .5 小數(直徑為奇數時半徑 = x.5,如 5÷2=2.5)。recompute 反算可驗。
 *   ② term 名詞認識(curated 池)—— 看敘述選出 圓心 / 半徑 / 直徑 / 圓周。grounded 課本定義。
 *   ③ judge 判斷對錯是非(curated 池)—— 「直徑是半徑的 2 倍」✓、「同一圓半徑都一樣長」✓、
 *      「圓心在圓周上」✗、「半徑比直徑長」✗…… 每題附正解(對/錯)+ 理由。
 *   ④ compass 圓規畫圓概念(✅ 可驗證)—— 圓規打開的長度 = 半徑。半徑 N → 打開 N;
 *      直徑 N → 打開 N÷2(含 .5)。recompute 反算可驗。
 *   ⑤ construct 圓規操作題(**列印題,線上不批改、只出現在 A4 卷**)—— 用圓規畫指定半徑/直徑
 *      的圓、標出半徑與直徑(留作圖方格)。線上版略過批改、標「請印出用圓規做」。
 *
 * 鐵則 / 設計(對齊 feedback_generator_source_lockdown,不打地鼠):
 *   - **seeded RNG**(別用 Math.random):同 seed → 同卷,可重現;UI「重抽」= 換 seed。
 *   - **答案程式可驗證**:換算 / 圓規概念題 answer 由 `recomputeCircleAnswer` 反算,validator 重算後比對。
 *   - **不超綱**:只到半徑 / 直徑 / 圓心 / 圓周 + ×2÷2 換算 + 圓規畫圓;**不出圓面積 / 圓周長公式
 *     (高年級)**。換算數值合理(半徑 1..20、直徑對應、小數只到 .5)。
 *   - **概念 / 判斷題 = curated 池**(每題標正解 + 理由,過國小三年級教師人設自審)。
 *   - **lockdown 防壞題**:模板 = 策展閉集常數;硬規則寫進產生器 + validator;200-seed regression 全過。
 *
 * 學習/遊戲分離:純學習側,**絕不**碰 commanderXp / 戰鬥數值。
 */

// ───────────────────────────── 型別 ─────────────────────────────

/** 題型(五種)。 */
export type CircleQuestionKind = 'convert' | 'term' | 'judge' | 'compass' | 'construct';

/**
 * 一題的反算規格(closed set)。validator / 線上批改依 `op` 重算 → 與 `answer` 比對。
 *   - 'radius-to-diameter':給半徑 r → 答 = 直徑 = r×2。
 *   - 'diameter-to-radius':給直徑 d → 答 = 半徑 = d÷2(d 為奇數時 = x.5)。
 *   - 'term-pick':名詞認識(從 choices 選 answer);curated,answerIndex 指定正解。
 *   - 'judge-tf':判斷對錯(isTrue 為真值);curated。
 *   - 'compass-radius':畫半徑 r 公分的圓 → 圓規打開 = r 公分。
 *   - 'compass-diameter':畫直徑 d 公分的圓 → 圓規打開 = 半徑 = d÷2 公分。
 *   - 'construct':圓規作圖題(列印用,線上不批改);只帶顯示資訊,recompute 回固定提示。
 */
export type CircleVerify =
  | { op: 'radius-to-diameter'; radius: number }
  | { op: 'diameter-to-radius'; diameter: number }
  | { op: 'term-pick'; choices: readonly string[]; answerIndex: number }
  | { op: 'judge-tf'; isTrue: boolean }
  | { op: 'compass-radius'; radius: number }
  | { op: 'compass-diameter'; diameter: number }
  | { op: 'construct'; shape: 'radius' | 'diameter'; size: number };

/** 一道圓題:題幹 + 程式可驗證答案 + 反算用 verify。 */
export interface CircleQuestion {
  id: string;
  /** 題幹(顯示 / 列印用)。 */
  prompt: string;
  /** 正解人類可讀字串(顯示 / 批改用)。construct(操作題)為列印提示語。 */
  answer: string;
  /**
   * 選擇 / 是非題的選項(term / judge 用;其餘題型 undefined)。
   * term:四個名詞;judge:固定 ['對', '錯']。
   */
  choices?: readonly string[];
  /** judge / term 的正解附帶理由(批改顯示用;換算 / 圓規題 undefined)。 */
  reason?: string;
  /** 反算規格(validator / 批改重算用)。 */
  verify: CircleVerify;
}

/** 一大題 = 一種題型 + 題目陣列 + 標籤 / 標題 / 配分。 */
export interface CircleSection {
  kind: CircleQuestionKind;
  /** 大題標籤,例 '一'。 */
  label: string;
  /** 大題標題,例 '半徑↔直徑換算'。 */
  title: string;
  /** 作答指示語。 */
  instructions: string;
  /** 每題配分。 */
  pointsEach: number;
  /**
   * 此大題是否「線上不批改、只出現在 A4 卷」(construct 圓規操作題)。
   * 線上互動卷會略過此大題的計分(或標示「請印出用圓規做」)。
   */
  printOnly?: boolean;
  /** 該大題的題目。 */
  questions: CircleQuestion[];
}

/** 一份圓考卷。 */
export interface CircleExam {
  id: string;
  title: string;
  /** 教科書版本(數學 = 康軒)。 */
  textbook: '康軒';
  /** 範圍標註(顯示用)。 */
  scope: string;
  sections: CircleSection[];
}

/** 各題型題數(預設見 DEFAULT_COUNTS;key = CircleQuestionKind)。 */
export type CircleCounts = Partial<Record<CircleQuestionKind, number>>;

/** `generateCircleExam` 選項。 */
export interface CircleGenOpts {
  /** 可重現種子(同 seed 同卷);UI「重抽」= 換 seed。 */
  seed: number;
  /** 各題型題數,預設見 DEFAULT_COUNTS。 */
  counts?: CircleCounts;
  /** 卷 id(預設 `circle-<seed>`)。 */
  id?: string;
  /** 卷名(預設 `圓考卷 · 第 <seed> 回`)。 */
  title?: string;
}

// ───────────────────────────── seeded RNG ─────────────────────────────

/**
 * 確定性 RNG —— mulberry32(與 mul-div-gen / stats-table-gen / time-exam-gen 同款,
 * 純函式可重現)。注入 seed,杜絕 Math.random。
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 抽一個元素。 */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/** 把數字格式化:整數不帶小數、x.5 帶一位小數(避免 2 顯示成 2.0)。 */
function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// ───────────────────────────── 受控閉集 ─────────────────────────────

/**
 * 半徑候選(整數,1..20):用於「半徑→直徑」「畫半徑 N」題,直徑 = r×2 ≤ 40,合三下範圍。
 */
const RADIUS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 18, 20] as const;

/**
 * 直徑候選(整數,含偶數與奇數):偶數 → 半徑整數;奇數 → 半徑 x.5(課本「直徑 5 → 半徑 2.5」)。
 * 鎖 ≤ 40,使半徑 ≤ 20。
 */
const DIAMETER_OPTIONS = [4, 5, 6, 8, 9, 10, 12, 14, 16, 18, 20, 24, 30, 40] as const;

/**
 * 圓規**實際作圖**(construct,列印題)的半徑候選 —— 鎖小範圍(1..6 公分),使
 * 真的能在 A4 方格中用圓規畫出(直徑題 = 半徑×2 ≤ 12 公分)。
 * 換算 / 概念題可用大數心算,但作圖必須是手能畫得出來的真實尺寸。
 */
const CONSTRUCT_RADIUS_OPTIONS = [2, 3, 4, 5, 6] as const;

/** ② 名詞認識 curated 池:每題一段課本定義式敘述 → 四選一(圓心/半徑/直徑/圓周)。 */
interface TermItem {
  /** 敘述(grounded 課本定義)。 */
  statement: string;
  /** 正解名詞。 */
  answer: '圓心' | '半徑' | '直徑' | '圓周';
}

const TERM_POOL: readonly TermItem[] = [
  { statement: '圍成一個圓的那一條曲線,叫做什麼?', answer: '圓周' },
  { statement: '圓最中心的那一個點,叫做什麼?', answer: '圓心' },
  { statement: '從圓心到圓周上一點的線段,叫做什麼?', answer: '半徑' },
  { statement: '通過圓心、兩端都在圓周上的線段,叫做什麼?', answer: '直徑' },
  { statement: '把圓形紙對摺兩次,兩條摺痕相交的那一點是什麼?', answer: '圓心' },
  { statement: '同一個圓裡,從圓心量到圓周、長度都一樣的那條線段是什麼?', answer: '半徑' },
  { statement: '一個圓裡最長的那一條線段是什麼?', answer: '直徑' },
  { statement: '畫圓時,圓規的筆會沿著哪一條線移動?', answer: '圓周' },
  { statement: '一條直徑剛好等於兩條什麼接起來?', answer: '半徑' },
  { statement: '射箭靶最中間、最接近圓中心的那一點是什麼?', answer: '圓心' },
] as const;

/** 名詞題固定四選項(順序固定,answerIndex 指向正解;不洗牌以保 recompute 穩定)。 */
const TERM_CHOICES = ['圓心', '半徑', '直徑', '圓周'] as const;

/** ③ 判斷對錯 curated 池:敘述 + 正解(對/錯)+ 理由。grounded 課本概念。 */
interface JudgeItem {
  statement: string;
  isTrue: boolean;
  reason: string;
}

const JUDGE_POOL: readonly JudgeItem[] = [
  { statement: '直徑是半徑的 2 倍。', isTrue: true, reason: '直徑 = 半徑×2,一條直徑由兩條半徑接成。' },
  { statement: '同一個圓裡,所有半徑都一樣長。', isTrue: true, reason: '半徑都是圓心到圓周的距離,同一圓裡一樣長。' },
  { statement: '直徑是一個圓裡面最長的線段。', isTrue: true, reason: '通過圓心的直徑比任何不過圓心的弦都長。' },
  { statement: '半徑 = 直徑 ÷ 2。', isTrue: true, reason: '直徑是半徑的 2 倍,反過來半徑 = 直徑÷2。' },
  { statement: '用圓規畫圓時,圓規打開的長度就是半徑。', isTrue: true, reason: '圓規針到筆的距離 = 半徑。' },
  { statement: '圓周是圍成圓的那一條曲線。', isTrue: true, reason: '圓周就是圓的邊(曲線)。' },
  { statement: '圓心在圓周上。', isTrue: false, reason: '圓心在圓的正中央,不在圓周(邊)上。' },
  { statement: '半徑比直徑長。', isTrue: false, reason: '半徑只有直徑的一半,直徑比較長。' },
  { statement: '同一個圓裡,直徑和半徑一樣長。', isTrue: false, reason: '直徑是半徑的 2 倍,不一樣長。' },
  { statement: '直徑不一定要通過圓心。', isTrue: false, reason: '直徑一定要通過圓心,沒過圓心的不是直徑。' },
  { statement: '畫直徑 8 公分的圓,圓規要打開 8 公分。', isTrue: false, reason: '要先算半徑 8÷2=4,圓規打開 4 公分。' },
  { statement: '圓周是一個點。', isTrue: false, reason: '圓周是一條曲線,不是點(圓心才是點)。' },
] as const;

/** 判斷題固定兩選項。 */
const JUDGE_CHOICES = ['對', '錯'] as const;

// ───────────────────────────── 反算核心(唯一真相)─────────────────────────────

/**
 * 依一題的 `verify` 規格,重算期望答案字串。validator 與線上批改共用此函式(單一真相)。
 * 規格不合法(越界 / 超綱…)回 null(視為壞題)。
 *
 * construct(圓規操作題)= 列印題,recompute 回固定提示語(不參與線上批改正確性)。
 */
export function recomputeCircleAnswer(v: CircleVerify): string | null {
  switch (v.op) {
    case 'radius-to-diameter': {
      if (!Number.isInteger(v.radius)) return null; // 半徑題用整數半徑
      if (v.radius < 1 || v.radius > 20) return null; // 不超綱(半徑 1..20)
      return `${fmtNum(v.radius * 2)} 公分`;
    }
    case 'diameter-to-radius': {
      if (!Number.isInteger(v.diameter)) return null; // 直徑題用整數直徑
      if (v.diameter < 2 || v.diameter > 40) return null; // 直徑 2..40 → 半徑 1..20
      return `${fmtNum(v.diameter / 2)} 公分`;
    }
    case 'term-pick': {
      if (!Array.isArray(v.choices) || v.choices.length < 2) return null;
      if (v.answerIndex < 0 || v.answerIndex >= v.choices.length) return null;
      return v.choices[v.answerIndex];
    }
    case 'judge-tf': {
      return v.isTrue ? '對' : '錯';
    }
    case 'compass-radius': {
      if (!Number.isInteger(v.radius)) return null;
      if (v.radius < 1 || v.radius > 20) return null;
      return `${fmtNum(v.radius)} 公分`;
    }
    case 'compass-diameter': {
      if (!Number.isInteger(v.diameter)) return null;
      if (v.diameter < 2 || v.diameter > 40) return null;
      return `${fmtNum(v.diameter / 2)} 公分`;
    }
    case 'construct': {
      if (v.shape !== 'radius' && v.shape !== 'diameter') return null;
      if (!Number.isInteger(v.size) || v.size < 1) return null;
      // 不超綱:半徑題 size = 半徑 ≤ 20;直徑題 size = 直徑 ≤ 40(對應半徑 ≤ 20)。
      const maxSize = v.shape === 'radius' ? 20 : 40;
      if (v.size > maxSize) return null;
      // 操作題:列印用,回固定提示(線上不批改),仍給「圓規打開幾公分」當批改參考。
      const open = v.shape === 'radius' ? v.size : v.size / 2;
      const which = v.shape === 'radius' ? '半徑' : '直徑';
      return `請印出用圓規畫:${which} ${v.size} 公分的圓(圓規打開 ${fmtNum(open)} 公分)`;
    }
  }
}

// ───────────────────────────── 各題型生成(從規格反算 answer)─────────────────────────────

/**
 * ① convert 半徑↔直徑換算。偶數題給半徑求直徑(×2);奇數題給直徑求半徑(÷2,
 * 直徑為奇數時半徑 = x.5)。**確保至少一題出現 .5 小數**(課本「直徑 5 → 半徑 2.5」)。
 */
function genConvertQuestions(rng: () => number, n: number): CircleQuestion[] {
  const out: CircleQuestion[] = [];
  for (let i = 0; i < n; i++) {
    // i=1 強制「直徑為奇數→半徑 x.5」(保證 .5 小數出現);其餘偶半徑、奇直徑交替。
    if (i % 2 === 0) {
      // 半徑 → 直徑(×2)。
      const radius = pick(rng, RADIUS_OPTIONS);
      out.push({
        id: `cv${i + 1}`,
        prompt: `一個圓的半徑是 ${radius} 公分,它的直徑是幾公分?`,
        answer: recomputeCircleAnswer({ op: 'radius-to-diameter', radius })!,
        verify: { op: 'radius-to-diameter', radius },
      });
    } else {
      // 直徑 → 半徑(÷2)。i===1 用奇數直徑(半徑 x.5),其餘隨機(可偶可奇)。
      const oddOnly = i === 1;
      const candidates = oddOnly ? DIAMETER_OPTIONS.filter((d) => d % 2 === 1) : DIAMETER_OPTIONS;
      const diameter = pick(rng, candidates);
      out.push({
        id: `cv${i + 1}`,
        prompt: `一個圓的直徑是 ${diameter} 公分,它的半徑是幾公分?`,
        answer: recomputeCircleAnswer({ op: 'diameter-to-radius', diameter })!,
        verify: { op: 'diameter-to-radius', diameter },
      });
    }
  }
  return out;
}

/**
 * ② term 名詞認識(curated 池,grounded 課本定義)。從 TERM_POOL 不重複抽 n 題,
 * 選項固定四名詞(answerIndex 指向正解)。
 */
function genTermQuestions(rng: () => number, n: number): CircleQuestion[] {
  const items = sampleDistinct(rng, TERM_POOL, n);
  return items.map((it, i) => {
    const answerIndex = TERM_CHOICES.indexOf(it.answer);
    return {
      id: `tm${i + 1}`,
      prompt: it.statement,
      answer: it.answer,
      choices: TERM_CHOICES,
      reason: `課本定義:${it.answer}。`,
      verify: { op: 'term-pick', choices: TERM_CHOICES, answerIndex },
    };
  });
}

/**
 * ③ judge 判斷對錯(curated 池)。從 JUDGE_POOL 不重複抽 n 題,選項固定 ['對','錯'],
 * 帶正解理由。**確保同時含「對」與「錯」**(抽到全同時補抽一題相反的)。
 */
function genJudgeQuestions(rng: () => number, n: number): CircleQuestion[] {
  let items = sampleDistinct(rng, JUDGE_POOL, n);
  // 保證至少各有一題「對」與一題「錯」(n≥2 時)。
  if (n >= 2) {
    const hasTrue = items.some((it) => it.isTrue);
    const hasFalse = items.some((it) => !it.isTrue);
    if (!hasTrue || !hasFalse) {
      const wantTrue = !hasTrue;
      const replacement = JUDGE_POOL.find(
        (p) => p.isTrue === wantTrue && !items.includes(p),
      );
      if (replacement) {
        items = [...items.slice(0, n - 1), replacement];
      }
    }
  }
  return items.map((it, i) => ({
    id: `jg${i + 1}`,
    prompt: it.statement,
    answer: it.isTrue ? '對' : '錯',
    choices: JUDGE_CHOICES,
    reason: it.reason,
    verify: { op: 'judge-tf', isTrue: it.isTrue },
  }));
}

/**
 * ④ compass 圓規畫圓概念(✅ 可驗證)。半徑題:畫半徑 N → 打開 N;直徑題:畫直徑 N →
 * 打開 N÷2(含 .5)。交替出兩型。
 */
function genCompassQuestions(rng: () => number, n: number): CircleQuestion[] {
  const out: CircleQuestion[] = [];
  for (let i = 0; i < n; i++) {
    if (i % 2 === 0) {
      const radius = pick(rng, RADIUS_OPTIONS);
      out.push({
        id: `cp${i + 1}`,
        prompt: `要畫一個半徑 ${radius} 公分的圓,圓規要打開幾公分?`,
        answer: recomputeCircleAnswer({ op: 'compass-radius', radius })!,
        verify: { op: 'compass-radius', radius },
      });
    } else {
      const diameter = pick(rng, DIAMETER_OPTIONS);
      out.push({
        id: `cp${i + 1}`,
        prompt: `要畫一個直徑 ${diameter} 公分的圓,圓規要打開幾公分?`,
        answer: recomputeCircleAnswer({ op: 'compass-diameter', diameter })!,
        verify: { op: 'compass-diameter', diameter },
      });
    }
  }
  return out;
}

/**
 * ⑤ construct 圓規操作題(**列印題,線上不批改**)。交替出「畫半徑 N 的圓」「畫直徑 N 的圓
 * 並標出半徑與直徑」。size 為整數,半徑 1..20(直徑題 size 為偶數使半徑為整數)。
 */
function genConstructQuestions(rng: () => number, n: number): CircleQuestion[] {
  const out: CircleQuestion[] = [];
  for (let i = 0; i < n; i++) {
    if (i % 2 === 0) {
      // 作圖半徑鎖小(1..6 公分),真的畫得出來。
      const size = pick(rng, CONSTRUCT_RADIUS_OPTIONS);
      out.push({
        id: `ct${i + 1}`,
        prompt: `用圓規在方格中畫一個半徑 ${size} 公分的圓,並用尺標出一條半徑。`,
        answer: recomputeCircleAnswer({ op: 'construct', shape: 'radius', size })!,
        verify: { op: 'construct', shape: 'radius', size },
      });
    } else {
      // 直徑用偶數(半徑整數,便於作圖);作圖半徑鎖小 ×2 → 直徑 ≤ 12 公分。
      const radius = pick(rng, CONSTRUCT_RADIUS_OPTIONS);
      const size = radius * 2;
      out.push({
        id: `ct${i + 1}`,
        prompt: `用圓規在方格中畫一個直徑 ${size} 公分的圓,並標出圓心、一條半徑和一條直徑。`,
        answer: recomputeCircleAnswer({ op: 'construct', shape: 'diameter', size })!,
        verify: { op: 'construct', shape: 'diameter', size },
      });
    }
  }
  return out;
}

/** 從池中不重複抽 n 個(n 超過池大小時取整池;seeded shuffle 前 n)。 */
function sampleDistinct<T>(rng: () => number, pool: readonly T[], n: number): T[] {
  const arr = [...pool];
  // Fisher–Yates(seeded)。
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, Math.min(n, arr.length));
}

// ───────────────────────────── 組卷 ─────────────────────────────

/**
 * 各題型題數(預設)。五大題(grounded 在課本第 6 章):
 *   convert 5×6=30(半徑↔直徑換算)/ term 5×4=20(名詞認識)/ judge 5×4=20(判斷對錯)/
 *   compass 4×5=20(圓規畫圓概念)/ construct 2×?(圓規操作,列印題不計線上分)。
 * 線上計分總分 = convert+term+judge+compass = 30+20+20+20 = 90(construct 列印題不入線上總分)。
 */
const DEFAULT_COUNTS: Required<CircleCounts> = {
  convert: 5,
  term: 4,
  judge: 5,
  compass: 4,
  construct: 2,
};

/** 固定出題順序(由淺入深):換算 → 名詞 → 判斷 → 圓規概念 → 圓規操作(列印)。 */
const SECTION_ORDER: CircleQuestionKind[] = ['convert', 'term', 'judge', 'compass', 'construct'];

const SECTION_META: Record<
  CircleQuestionKind,
  { title: string; instructions: string; pointsEach: number; printOnly?: boolean }
> = {
  convert: {
    title: '半徑與直徑的換算',
    instructions:
      '在同一個圓裡,直徑 = 半徑 × 2、半徑 = 直徑 ÷ 2。算出答案,記得寫單位「公分」(直徑是奇數時半徑會有小數,例如 5÷2=2.5)。每題 6 分。',
    pointsEach: 6,
  },
  term: {
    title: '認識圓各部分的名稱',
    instructions:
      '圓有圓心、圓周、半徑、直徑。讀題目的敘述,從「圓心 / 半徑 / 直徑 / 圓周」中選出正確的名稱。每題 5 分。',
    pointsEach: 5,
  },
  judge: {
    title: '判斷對錯',
    instructions:
      '讀每一句話,對的寫「對」、錯的寫「錯」。想想半徑和直徑的關係、圓心與圓周的位置。每題 4 分。',
    pointsEach: 4,
  },
  compass: {
    title: '用圓規畫圓',
    instructions:
      '圓規打開的長度就是半徑。畫半徑 N 公分的圓,圓規打開 N 公分;畫直徑 N 公分的圓,要先算半徑(N÷2)再打開。每題 5 分。',
    pointsEach: 5,
  },
  construct: {
    title: '圓規操作(請印出後用圓規實際作圖)',
    instructions:
      '這一大題請列印出考卷,用圓規在方格中實際畫圓並標出半徑、直徑。線上不批改,由家長對照解答檢查。',
    pointsEach: 0,
    printOnly: true,
  },
};

const CN_SECTION_LABELS = ['一', '二', '三', '四', '五', '六'];

/**
 * 從受控閉集生一份圓考卷。同 seed → 同卷(可重現)。
 *
 * @param opts seed(必填)+ 可選題數 / id / title。
 * @returns 一份 `CircleExam`(預設五大題:換算 / 名詞 / 判斷 / 圓規概念 / 圓規操作)。
 *   生成後建議過 `validateCircleExam`。
 */
export function generateCircleExam(opts: CircleGenOpts): CircleExam {
  const rng = makeRng(opts.seed);
  const counts: Required<CircleCounts> = { ...DEFAULT_COUNTS, ...opts.counts };

  const sections: CircleSection[] = SECTION_ORDER.map((kind, idx) => {
    const meta = SECTION_META[kind];
    const nn = counts[kind];
    let questions: CircleQuestion[];
    if (kind === 'convert') {
      questions = genConvertQuestions(rng, nn);
    } else if (kind === 'term') {
      questions = genTermQuestions(rng, nn);
    } else if (kind === 'judge') {
      questions = genJudgeQuestions(rng, nn);
    } else if (kind === 'compass') {
      questions = genCompassQuestions(rng, nn);
    } else {
      questions = genConstructQuestions(rng, nn);
    }
    return {
      kind,
      label: CN_SECTION_LABELS[idx] ?? String(idx + 1),
      title: meta.title,
      instructions: meta.instructions,
      pointsEach: meta.pointsEach,
      printOnly: meta.printOnly,
      questions,
    };
  });

  return {
    id: opts.id ?? `circle-${opts.seed}`,
    title: opts.title ?? `圓考卷 · 第 ${opts.seed} 回`,
    textbook: '康軒',
    scope: '三下 · 圓(半徑↔直徑換算 / 名詞認識 / 判斷對錯 / 用圓規畫圓 / 圓規操作)',
    sections,
  };
}

/**
 * 一份考卷的線上計分總分(Σ pointsEach×題數)。construct(printOnly)pointsEach=0,
 * 故自然不計入(列印操作題線上不批改)。
 */
export function examTotalPoints(exam: CircleExam): number {
  return exam.sections.reduce((sum, s) => sum + s.pointsEach * s.questions.length, 0);
}

// ───────────────────────────── 驗證器 ─────────────────────────────

/**
 * 驗證一份(產生)圓考卷是否合法。回**錯誤訊息清單**,空陣列 = 合法。
 *
 * 檢查(lockdown 不打地鼠):
 *   - id 唯一、prompt / answer 非空、每大題至少一題。
 *   - **每題 answer == recomputeCircleAnswer(verify)**(反算一致)。
 *   - 不超綱硬規則:半徑 1..20、直徑 2..40(= 半徑×2);**無圓面積 / 圓周長公式 op**。
 *   - 換算正確:直徑 = 半徑×2、半徑 = 直徑÷2。
 *   - 選擇 / 是非題:正解在 choices 內;判斷題 choices = ['對','錯']。
 */
export function validateCircleExam(exam: CircleExam): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  if (exam.sections.length === 0) errors.push('考卷沒有任何大題');

  for (const section of exam.sections) {
    if (section.questions.length === 0) {
      errors.push(`${section.label}、${section.title}:沒有任何題目`);
    }
    for (const q of section.questions) {
      if (seenIds.has(q.id)) errors.push(`${q.id}: 重複的題 id`);
      seenIds.add(q.id);
      if (!q.prompt || q.prompt.trim() === '') {
        errors.push(`${q.id}: prompt 為空`);
        continue;
      }
      if (!q.answer || q.answer.trim() === '') {
        errors.push(`${q.id}: answer 為空`);
        continue;
      }
      const expected = recomputeCircleAnswer(q.verify);
      if (expected === null) {
        errors.push(`${q.id}: verify 規格不合法(${q.verify.op})`);
        continue;
      }
      if (expected !== q.answer) {
        errors.push(`${q.id}: 反算「${expected}」≠ answer「${q.answer}」(${q.verify.op})`);
      }
      checkBounds(q, errors);
    }
  }

  return errors;
}

/** 各題型「不超綱」硬規則(半徑 1..20、直徑 2..40、換算正確、選項一致)。 */
function checkBounds(q: CircleQuestion, errors: string[]): void {
  const v = q.verify;
  if (v.op === 'radius-to-diameter') {
    if (v.radius < 1 || v.radius > 20) errors.push(`${q.id}: 半徑超出範圍(${v.radius},須 1..20)`);
  } else if (v.op === 'diameter-to-radius') {
    if (v.diameter < 2 || v.diameter > 40) {
      errors.push(`${q.id}: 直徑超出範圍(${v.diameter},須 2..40)`);
    }
  } else if (v.op === 'compass-radius') {
    if (v.radius < 1 || v.radius > 20) errors.push(`${q.id}: 圓規半徑超出範圍(${v.radius})`);
  } else if (v.op === 'compass-diameter') {
    if (v.diameter < 2 || v.diameter > 40) errors.push(`${q.id}: 圓規直徑超出範圍(${v.diameter})`);
  } else if (v.op === 'term-pick') {
    if (v.answerIndex < 0 || v.answerIndex >= v.choices.length) {
      errors.push(`${q.id}: 名詞題正解索引越界`);
    }
    if (q.choices && q.answer && !q.choices.includes(q.answer)) {
      errors.push(`${q.id}: 名詞題正解不在選項中`);
    }
  } else if (v.op === 'judge-tf') {
    if (q.answer !== '對' && q.answer !== '錯') errors.push(`${q.id}: 判斷題正解非「對/錯」`);
    if (!q.reason || q.reason.trim() === '') errors.push(`${q.id}: 判斷題缺正解理由`);
  } else if (v.op === 'construct') {
    const maxSize = v.shape === 'radius' ? 20 : 40;
    if (v.size < 1 || v.size > maxSize) {
      errors.push(`${q.id}: 圓規操作 size 超出範圍(${v.size},${v.shape} 上限 ${maxSize})`);
    }
  }
  // 不可能出現圓面積 / 圓周長 op(型別封閉集已排除);此處不需額外檢查。
}
